import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Restoration, RestorationPrice } from 'models/order';
import { Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { formatCurrency, formatNumber } from 'utils/format';

type PriceSpecificationSectionProps = {
    /** DD-2506: the rows as the server priced them for the chosen lab (what a save stores). */
    restorations: Restoration[];
    totalPrice: number;
    currency: string;
};

type PriceLine = {
    key: string;
    description: string;
    quotable: boolean;
    isTioTandvard: boolean;
    price: RestorationPrice;
};

const vatOf = (price: RestorationPrice) => (price.totalWithVat ?? 0) - (price.totalWithoutVat ?? 0);

const PriceSpecificationSection: FC<PriceSpecificationSectionProps> = ({
    restorations,
    totalPrice,
    currency,
}: PriceSpecificationSectionProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);

    // Materials first, then actions, as the review always listed them.
    const lines = useMemo((): PriceLine[] => {
        const materials = restorations.flatMap((restoration) =>
            (restoration.materials ?? []).map((row) => ({
                key: `m-${row._id}`,
                description: row.name,
                quotable: false,
                isTioTandvard: Boolean(row.isTioTandvard),
                price: row.price,
            })),
        );
        const actions = restorations.flatMap((restoration) =>
            (restoration.actions ?? []).map((row) => ({
                key: `a-${row._id}`,
                description: `${row.code || ''} ${row.name || ''}`.trim(),
                quotable: Boolean(row.quotable),
                isTioTandvard: Boolean(row.isTioTandvard),
                price: row.price,
            })),
        );
        return [...materials, ...actions];
    }, [restorations]);

    return (
        <TableContainer>
            <Table size="small">
                <TableHead sx={{ borderBottom: 'none' }}>
                    <TableRow>
                        <TableCell>{translate('common:DESCRIPTION')}</TableCell>
                        <TableCell align="right">{translate('orders:QUANTITY')}</TableCell>
                        <TableCell align="right">{translate('orders:UNIT_PRICE')}</TableCell>
                        <TableCell align="right">{translate('orders:VAT')}</TableCell>
                        <TableCell align="right">{translate('orders:TOTAL')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {lines.map((line) => (
                        <TableRow key={line.key}>
                            <TableCell>
                                <Box display="flex" alignItems="center" gap={1} width="100%">
                                    <Box sx={{ minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                        {line.description}
                                    </Box>
                                    {line.quotable && (
                                        <Box flexShrink={0} display="flex" alignItems="flex-start">
                                            <Chip label={translate('common:QUOTABLE')} size="small" />
                                        </Box>
                                    )}
                                    {line.isTioTandvard && (
                                        <Box flexShrink={0} display="flex" alignItems="flex-start">
                                            <Chip label={translate('orders:TIOTANDVARD_PRICE')} size="small" />
                                        </Box>
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell align="right">{formatNumber(line.price.quantity || 0)}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                                {formatCurrency(line.price.unitPrice ?? 0, currency)}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                                {formatCurrency(vatOf(line.price), currency)}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                                {formatCurrency(line.price.totalWithVat ?? 0, currency)}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow sx={{ borderTop: '2px solid #bbb' }}>
                        <TableCell colSpan={4} align="right" sx={{ borderBottom: 0 }}>
                            <strong>{translate('orders:GRAND_TOTAL')}</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', borderBottom: 0 }}>
                            <strong>{formatCurrency(totalPrice, currency)}</strong>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};
export default PriceSpecificationSection;
